"use client"

import React, { FC, useEffect, useState } from "react"
import { useCreateChatClient } from "stream-chat-react"
import { MessageModalLoading } from "./MessageModalLoading"
import { useSession } from "@/hooks/useSession"
import { MessageModalChannel } from "./MessageModalChannel"
import { MessageModalInitialMessage } from "./MessageModalInitialMessage"
import { Channel } from "stream-chat"
import { FragmentType, graphql, useFragment } from "@/gql"
import type { NovaSession } from "@/types"
import { captureException } from "@sentry/nextjs"

const MessageModalContentWrapperFragment = graphql(`
  fragment MessageModalContentWrapper on User {
    id
    uuid

    creative {
      id
    }

    ...MessageModalInitialMessage
  }
`)

interface Props {
  user: FragmentType<typeof MessageModalContentWrapperFragment> | undefined | null
  messagingToken: string
  closeModal: () => void
  serverSession: NovaSession
  applicationId?: string
  jobId?: string
  trackingEntryPoint: string
}

const apiKey = process.env.NEXT_PUBLIC_STREAM_CHAT_API_KEY!

const jobNetworkingChannels = ["client_job", "creative_job"]

type UiContent = "initialMessage" | "channel"

/*
 * This component handles initialising the chat client
 * and rendering logic to show initial message input or the chat channel
 * if it's not the first message.
 */
export const MessageModalContentWrapper: FC<Props> = ({
  messagingToken,
  closeModal,
  serverSession,
  applicationId,
  trackingEntryPoint,
  jobId,
  ...props
}) => {
  const user = useFragment(MessageModalContentWrapperFragment, props.user)
  const [uiContent, setUiContent] = useState<UiContent | null>(null)
  const session = useSession()
  const [channel, setChannel] = useState<Channel | null>(null)

  const showChatChannel = uiContent === "channel"

  const client = useCreateChatClient({
    apiKey,
    tokenOrProvider: messagingToken,
    userData: { id: serverSession.user.uuid },
  })

  const sessionUserIsClient = session?.data?.account === "client"

  let channelType
  if (applicationId) {
    channelType = sessionUserIsClient ? "client_job" : "creative_job"
  } else {
    channelType = sessionUserIsClient ? "client_to_creative_networking" : "creative_networking"
  }

  useEffect(() => {
    const initializeUiContent = async () => {
      if (!client) return
      let channel = null
      let channels: any[] = []
      try {
        const existingChannels = await client.queryChannels({
          members: {
            $eq: [client.userID!, user?.uuid!],
          },
        })

        // FIXME: For now we need to filter on the frontend because stream timesout with a filter
        channels = existingChannels.filter(channel => channel.type === channelType)

        channel = channels[0]
      } catch (e) {
        channel = client.channel(channelType, {
          members: [client.userID!, user?.uuid!],
        })

        // Timeout error
        captureException(e)
      }

      const applicationChannel = applicationId
        ? // need to augment the module to add our custom data
          channels.find(channel => channel.data.job_application_id === applicationId)
        : null

      if (!applicationChannel && jobNetworkingChannels.includes(channelType)) {
        setUiContent("initialMessage")
      } else if (applicationChannel) {
        setChannel(applicationChannel)
        setUiContent("channel")
      } else if (channel) {
        setChannel(channel)
        setUiContent("channel")
      } else {
        setUiContent("initialMessage")
      }
    }

    if (client && !uiContent) {
      initializeUiContent()
    }
  }, [client, sessionUserIsClient, uiContent, user, channelType, applicationId])

  if (!client || !uiContent) {
    return <MessageModalLoading />
  }

  return (
    <div className="flex h-full flex-1 flex-col">
      {showChatChannel ? (
        <MessageModalChannel channel={channel} client={client} />
      ) : (
        <MessageModalInitialMessage
          applicationId={applicationId}
          closeModal={closeModal}
          user={user}
          jobId={jobId}
          trackingEntryPoint={trackingEntryPoint}
        />
      )}
    </div>
  )
}
